<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import reportsAPI from 'dashboard/api/reports';
import LineReportsTable from './components/LineReportsTable.vue';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const { t } = useI18n();

const isLoading = ref(false);
const reportData = ref(null);
const since = ref(0);
const until = ref(0);

const summary = computed(() => reportData.value?.summary || {});
const hasData = computed(() => (reportData.value?.data || []).length > 0);

const fmtNum = v => Number(v || 0).toLocaleString();
const fmtPct = v => `${Number(v || 0).toFixed(2)}%`;

const fetchReportData = async (sinceTs, untilTs) => {
  isLoading.value = true;
  try {
    const response = await reportsAPI.getLineDelivery({
      since: sinceTs,
      until: untilTs,
    });
    reportData.value = response.data;
  } catch {
    useAlert(t('LINE_REPORTS.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const onFilterChange = ({ from, to }) => {
  since.value = from;
  until.value = to;
  if (from && to) fetchReportData(from, to);
};

const onDownloadClick = () => {
  if (!hasData.value) {
    useAlert(t('LINE_REPORTS.NO_DATA_TO_DOWNLOAD'));
    return;
  }
  const headers = [
    'Date',
    'Brand',
    'Total',
    'Reply (free)',
    'Push (paid)',
    'Push %',
    'Top Admin',
  ];
  const rows = (reportData.value?.data || []).map(row => [
    row.date,
    row.inbox_name,
    row.total,
    row.reply_count,
    row.push_count,
    `${row.push_percentage}%`,
    row.top_admin
      ? `${row.top_admin.name} (${row.top_admin.push_count})`
      : '--',
  ]);
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
  ].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `line_delivery_report_${new Date().toISOString().split('T')[0]}.csv`;
  a.style.visibility = 'hidden';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

onMounted(() => {
  // CompactDateInboxFilters will fire onFilterChange on mount with Last 7 days
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="lin-page">
    <div class="lin-shell">
      <div class="lin-header">
        <div class="lin-header-left">
          <button
            class="lin-side-toggle"
            type="button"
            aria-label="Toggle side navigation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 4v16M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
              />
            </svg>
          </button>
          <h1 class="lin-title">{{ $t('LINE_REPORTS.HEADER') }}</h1>
        </div>
        <button
          class="lin-dl-btn"
          type="button"
          :disabled="!hasData || isLoading"
          @click="onDownloadClick"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              stroke="currentcolor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
            />
          </svg>
          <span>{{ $t('LINE_REPORTS.DOWNLOAD') }}</span>
        </button>
      </div>

      <div class="lin-toolbar">
        <CompactDateInboxFilters
          :show-inbox="false"
          @filter-change="onFilterChange"
        />
      </div>

      <div class="lin-content">
        <div class="lin-kpis">
          <div class="lin-kpi">
            <div class="lin-kpi-label">
              {{ $t('LINE_REPORTS.SUMMARY.TOTAL') }}
            </div>
            <div class="lin-kpi-value">
              {{ isLoading ? '—' : fmtNum(summary.total) }}
            </div>
          </div>
          <div class="lin-kpi">
            <div class="lin-kpi-label">
              {{ $t('LINE_REPORTS.SUMMARY.REPLY_FREE') }}
            </div>
            <div class="lin-kpi-value lin-kpi-value--green">
              {{ isLoading ? '—' : fmtNum(summary.reply_count) }}
            </div>
          </div>
          <div class="lin-kpi">
            <div class="lin-kpi-label">
              {{ $t('LINE_REPORTS.SUMMARY.PUSH_PAID') }}
            </div>
            <div class="lin-kpi-value lin-kpi-value--red">
              {{ isLoading ? '—' : fmtNum(summary.push_count) }}
            </div>
          </div>
          <div class="lin-kpi">
            <div class="lin-kpi-label">
              {{ $t('LINE_REPORTS.SUMMARY.PUSH_PERCENTAGE') }}
            </div>
            <div class="lin-kpi-value">
              {{ isLoading ? '—' : fmtPct(summary.push_percentage) }}
            </div>
          </div>
        </div>

        <section class="lin-card">
          <div class="lin-card-header">
            <div class="lin-card-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 7h18M3 12h18M3 17h18"
                />
              </svg>
              <span>Delivery log</span>
            </div>
          </div>
          <div class="lin-table-wrap">
            <LineReportsTable
              :data="reportData?.data || []"
              :is-loading="isLoading"
            />
          </div>
        </section>

        <p class="lin-footer">All reports are displayed in your local time.</p>
      </div>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

<style scoped>
.lin-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}

.lin-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.lin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}

.lin-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.lin-side-toggle {
  width: 40px;
  height: 40px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #111827;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.lin-side-toggle:hover {
  background: #f3f4f6;
}

.lin-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.lin-dl-btn {
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.lin-dl-btn:hover:not(:disabled) {
  background: #f9fafb;
}
.lin-dl-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.lin-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
}

.lin-content {
  padding: 16px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  /* Force light-mode CSS variables for LineReportsTable */
  --n-slate-12: #111827;
  --n-slate-11: #374151;
  --n-slate-10: #6b7280;
  --n-solid-2: #f9fafb;
  --n-alpha-2: rgba(0, 0, 0, 0.04);
  --n-weak: rgba(0, 0, 0, 0.1);
}

.lin-content :deep(.dark\:text-white) {
  color: #111827 !important;
}
.lin-content :deep(.dark\:text-white\/60) {
  color: #6b7280 !important;
}
.lin-content :deep(.text-n-slate-12) {
  color: #111827 !important;
}
.lin-content :deep(.text-n-slate-11) {
  color: #374151 !important;
}
.lin-content :deep(.bg-n-solid-2) {
  background: #ffffff !important;
}
.lin-content :deep(.bg-n-alpha-2) {
  background: #f8fafc !important;
}
.lin-content :deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
.lin-content :deep(.divide-n-weak > *) {
  border-color: #e5e7eb !important;
}
.lin-content :deep(th) {
  color: #111827 !important;
  background: #f8fafc !important;
  font-weight: 600 !important;
  text-transform: none !important;
}
.lin-content :deep(td) {
  color: #374151 !important;
}
/* Remove the dark outer card bg from LineReportsTable wrapper */
.lin-table-wrap :deep(.rounded-xl) {
  border-radius: 4px !important;
  background: #ffffff !important;
}
.lin-table-wrap :deep(.shadow) {
  box-shadow: none !important;
}
.lin-table-wrap :deep(.rounded-xl > div:first-child) {
  display: none;
}

/* ── KPI cards ── */
.lin-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.lin-kpi {
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid #ebedf0;
  background: #f3f4f6;
}

.lin-kpi-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  line-height: 1.3;
}

.lin-kpi-value {
  margin-top: 8px;
  font-size: 28px;
  line-height: 1.05;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.03em;
}

.lin-kpi-value--green {
  color: #059669;
}
.lin-kpi-value--red {
  color: #dc2626;
}

/* ── Table card ── */
.lin-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  overflow: hidden;
}

.lin-card-header {
  min-height: 56px;
  padding: 0 16px;
  border-bottom: 1px solid #dfe3e8;
  display: flex;
  align-items: center;
}

.lin-card-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.lin-table-wrap {
  overflow-x: auto;
}

.lin-footer {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

/* ── Dark override ── */
::global(body.dark) .lin-page,
::global(body.dark) .lin-shell,
::global(body.dark) .lin-header,
::global(body.dark) .lin-card,
::global(body.theme-mix) .lin-page,
::global(body.theme-mix) .lin-shell,
::global(body.theme-mix) .lin-header,
::global(body.theme-mix) .lin-card {
  background: #ffffff !important;
  color: #111827 !important;
}

@media (max-width: 1100px) {
  .lin-content {
    padding: 12px 16px 16px;
  }
  .lin-toolbar {
    padding: 10px 16px 8px;
  }
  .lin-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
